import * as IF from "infrontjs";
import template from "./detail.html";
import { _lcs, locale } from "../../i18n";

// Every *_LABELS map below holds the German source string, which is also the
// dictionary key. They are module constants — evaluated before the app and its
// I18n exist — so the translation happens where they are read, not here.

// User status -> display label.
const STATUS_LABELS = {
    ok            : "Aktiv",
    not_validated : "Nicht verifiziert",
    blocked       : "Gesperrt",
    banned        : "Gebannt"
};

// Role key -> display label.
const ROLE_LABELS = {
    admin  : "Admin",
    client : "Client",
    guest  : "Gast"
};

// Accommodation status -> display label.
const ACC_STATUS_LABELS = {
    draft     : "Entwurf",
    published : "Veröffentlicht"
};

// Region key -> display label.
const REGION_LABELS = {
    saechsische_schweiz : "Sächsische Schweiz"
};

/**
 * <client-user-detail> — read-only modal showing the details of a client
 * (Gastgeber) user, including the list of their accommodations.
 *
 * Usage:
 *   el.open( user )
 */
class ClientUserDetail extends HTMLElement
{
    constructor()
    {
        super();
        this._connected = false;
    }

    connectedCallback()
    {
        if ( false === this._connected )
        {
            this.app = IF.App.get();
            this.render();
            this.bind();
            this._connected = true;
        }
    }

    render()
    {
        this.app.view.render( this, template, {} );
        this._modal = this.querySelector( '[data-id="cd-modal"]' );
    }

    bind()
    {
        this.querySelector( '[data-id="cd-close"]' ).addEventListener( "click", () => this.close() );
        this.querySelector( '[data-id="cd-close-btn"]' ).addEventListener( "click", () => this.close() );
        this.querySelector( '[data-id="cd-backdrop"]' ).addEventListener( "click", () => this.close() );

        this._onKeydown = ( e ) =>
        {
            if ( e.key === "Escape" && this._modal && !this._modal.hidden )
            {
                this.close();
            }
        };
        document.addEventListener( "keydown", this._onKeydown );
    }

    disconnectedCallback()
    {
        if ( this._onKeydown )
        {
            document.removeEventListener( "keydown", this._onKeydown );
        }
    }

    /* ------------------------------- Public API ------------------------------ */

    open( user )
    {
        if ( !user ) return;

        this._set( "cd-name", this._displayName( user ) || "—" );
        this._set( "cd-company", user.organisationName || "—" );
        this._set( "cd-email", user.email || "—" );
        this._html( "cd-roles", this._rolesBadgesHtml( user.role ) );
        this._set( "cd-status", this._statusLabel( user.status ) );
        this._html( "cd-accommodations", this._accommodationsListHtml( user ) );
        this._set( "cd-created", this._formatDate( user.createdAt ) );
        this._set( "cd-updated", this._formatDate( user.updatedAt ) );
        this._set( "cd-id", user.id || "—" );

        this._modal.hidden = false;
        this.querySelector( '[data-id="cd-close-btn"]' ).focus();
    }

    close()
    {
        this._modal.hidden = true;
    }

    /* ------------------------------- Helpers -------------------------------- */

    _set( id, value )
    {
        const el = this.querySelector( '[data-id="' + id + '"]' );
        if ( el ) el.textContent = value;
    }

    _html( id, value )
    {
        const el = this.querySelector( '[data-id="' + id + '"]' );
        if ( el ) el.innerHTML = value;
    }

    _displayName( user )
    {
        return ( user && user.data && user.data.name ) ? String( user.data.name ) : "";
    }

    _statusLabel( status )
    {
        return STATUS_LABELS[ status ] ? _lcs( STATUS_LABELS[ status ] ) : ( status || "—" );
    }

    /**
     * Render the role value (array or comma-separated string) as colored badges.
     */
    _rolesBadgesHtml( value )
    {
        const roles = Array.isArray( value )
            ? value
            : String( value || "" ).split( "," ).map( ( r ) => r.trim() ).filter( Boolean );

        if ( roles.length === 0 )
        {
            return '<span class="role-badge role-badge--none">—</span>';
        }

        const badges = roles.map( ( r ) =>
        {
            const key = String( r ).toLowerCase();
            const known = ROLE_LABELS.hasOwnProperty( key );
            const label = known ? this._escape( _lcs( ROLE_LABELS[ key ] ) ) : this._escape( r );
            const mod = known ? key : "other";
            return '<span class="role-badge role-badge--' + mod + '">' + label + '</span>';
        } );

        return '<span class="role-badges">' + badges.join( "" ) + '</span>';
    }

    /**
     * List of the client's accommodations, showing status, name and region.
     * Sorted by status, then name.
     */
    _accommodationsListHtml( user )
    {
        const list = ( user && Array.isArray( user.accommodations ) ) ? user.accommodations.slice() : [];
        if ( list.length === 0 )
        {
            return '<span class="acc-list__empty">' + this._escape( _lcs( "Keine Unterkünfte" ) ) + '</span>';
        }

        list.sort( ( a, b ) =>
            String( a.status || "" ).localeCompare( String( b.status || "" ) ) ||
            String( a.name || "" ).localeCompare( String( b.name || "" ) )
        );

        const items = list.map( ( acc ) =>
        {
            const statusKey = String( acc.status || "" ).toLowerCase();
            const statusLabel = ACC_STATUS_LABELS[ statusKey ]
                ? _lcs( ACC_STATUS_LABELS[ statusKey ] ) : ( acc.status || "—" );
            const region = REGION_LABELS[ acc.region ] ? _lcs( REGION_LABELS[ acc.region ] ) : ( acc.region || "—" );
            return '<li class="acc-list__item">' +
                '<span class="acc-status acc-status--' + this._escape( statusKey ) + '">' + this._escape( statusLabel ) + '</span>' +
                '<span class="acc-list__name" title="' + this._escape( acc.name || "" ) + '">' + this._escape( acc.name || "—" ) + '</span>' +
                '<span class="acc-list__region">' + this._escape( region ) + '</span>' +
            '</li>';
        } );

        return '<ul class="acc-list">' + items.join( "" ) + '</ul>';
    }

    _escape( str )
    {
        return String( str == null ? "" : str ).replace( /[&<>"']/g, ( c ) =>
            ( { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[ c ] ) );
    }

    _formatDate( value )
    {
        if ( !value ) return "—";
        const d = new Date( value );
        if ( isNaN( d.getTime() ) ) return String( value );
        return new Intl.DateTimeFormat( locale(), {
            day: "2-digit", month: "2-digit", year: "numeric",
            hour: "2-digit", minute: "2-digit"
        } ).format( d );
    }
}

customElements.define( "client-user-detail", ClientUserDetail );
